import { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { ModalShell, ModalInputField } from "../components/ModalShell";
import { MonthYearPicker } from "../components/MonthYearPicker";
import { FileUpload } from "../components/FileUpload";
import {
  ProposeChangeNotice,
  ReviewLockNotice,
} from "../components/ReviewLockNotice";
import { canRequestChange, reviewedFields } from "../utils/credentialEdit";
import {
  createCredential,
  getCredential,
  updateCredential,
} from "../api/credentials";
import { createDownloadUrl } from "../api/storage";
import { reviewLockNotice } from "../utils/credentialLock";

export function AddOtherPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    title: "",
    issuer: "",
    credentialId: "",
    url: "",
    startDate: "",
    endDate: "",
    description: "",
  });
  const [certificate, setCertificate] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [changeable, setChangeable] = useState(false);
  const isVerified = status === "VERIFIED";
  const isView =
    location.pathname.includes("/view/") || searchParams.get("view") === "1";
  const isViewOnly = isView;
  const proposing =
    changeable && status === "VERIFIED" && searchParams.get("change") === "1";
  const locked =
    isViewOnly || (Boolean(reviewLockNotice(status)) && !proposing);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const c = await getCredential(id);
        setForm({
          title: c.title ?? "",
          issuer: c.issuer ?? "",
          credentialId: c.metadata?.credentialId ?? "",
          url: c.metadata?.url ?? "",
          startDate: c.startDate ? c.startDate.slice(0, 7) : "",
          endDate: c.endDate ? c.endDate.slice(0, 7) : "",
          description: c.description ?? "",
        });
        setStatus(c.status);
        setChangeable(canRequestChange(c));
        if (c.certificateS3Key) {
          setCertificate({
            s3Key: c.certificateS3Key,
            fileName: c.certificateFileName,
          });
        }
      } catch (err) {
        setError(err.message ?? "Failed to load credential");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function handleClose() {
    navigate("/profile");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (isViewOnly) return;
    if (locked) {
      if (!form.description.trim()) return setError("Add a description.");
      return save({ description: form.description.trim() });
    }

    if (!form.title.trim()) return setError("Enter the credential title.");
    if (!form.issuer.trim()) return setError("Enter the issuing body.");
    if (!form.credentialId.trim()) return setError("Enter the credential ID.");
    if (!form.url.trim()) return setError("Enter the verification URL.");
    if (!form.startDate) return setError("Pick the issue month and year.");
    if (!form.endDate) return setError("Pick the expiry month and year.");
    if (!form.description.trim()) return setError("Add a description.");
    if (!certificate?.s3Key) {
      return setError("Upload the certificate. Verification requires proof.");
    }

    return save({
      type: "OTHER",
      title: form.title.trim(),
      issuer: form.issuer.trim(),
      startDate: `${form.startDate}-01`,
      endDate: `${form.endDate}-01`,
      description: form.description.trim(),
      metadata: {
        credentialId: form.credentialId.trim(),
        url: form.url.trim(),
      },
      certificateS3Key: certificate.s3Key,
      certificateFileName: certificate.fileName,
    });
  }

  /** Adds the credential, or saves the changes to the one being edited. */
  async function save(payload) {
    if (proposing) {
      navigate("/checkout", {
        state: {
          type: "VERIFICATION_FEE",
          credentialId: id,
          editChanges: reviewedFields(payload),
        },
      });
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        await updateCredential(id, payload);
      } else {
        await createCredential(payload);
      }

      handleClose();
    } catch (err) {
      setError(err.message ?? "Failed to save");
      setSubmitting(false);
    }
  }

  return (
    <AppShell>
      <ModalShell
        title={
          isViewOnly
            ? "View Credential"
            : proposing
              ? "Change verified details"
              : isEdit && isVerified
                ? "Edit Additional Info"
                : isEdit
                  ? "Edit Credential"
                  : "Add Other Credential"
        }
        subtitle={
          isViewOnly
            ? "Certification, license & filled details (view-only)"
            : proposing
              ? "Request a change to verified details (verification fee applies)"
              : isEdit && isVerified
                ? "Update additional info (verified details are locked)"
                : "Short courses, certifications, professional licences, and achievements"
        }
        onClose={handleClose}
        maxWidth="max-w-xl"
      >
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            Loading credential details...
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col flex-1 overflow-hidden"
          >
            {/* Form Fields Body */}
            <div className="px-6 py-5 overflow-y-auto space-y-4 flex-1">
              {proposing ? (
                <ProposeChangeNotice onCancel={() => setSearchParams({})} />
              ) : (
                <ReviewLockNotice
                  status={status}
                  onRequestChange={
                    status === "VERIFIED" && changeable
                      ? () => setSearchParams({ change: "1" })
                      : undefined
                  }
                />
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ModalInputField
                  label="Title"
                  placeholder="e.g. AWS Certified Solutions Architect"
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                  disabled={locked}
                  required
                />
                <ModalInputField
                  label="Issuing Body / Organization"
                  placeholder="e.g. Amazon Web Services"
                  value={form.issuer}
                  onChange={(e) => update("issuer", e.target.value)}
                  disabled={locked}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ModalInputField
                  label="Credential ID"
                  placeholder="e.g. AWS-12345"
                  value={form.credentialId}
                  onChange={(e) => update("credentialId", e.target.value)}
                  disabled={locked}
                  required
                />
                <ModalInputField
                  label="Verification URL"
                  placeholder="e.g. https://credly.com/..."
                  value={form.url}
                  onChange={(e) => update("url", e.target.value)}
                  type="url"
                  disabled={locked}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MonthYearPicker
                  label="Issue Month & Year"
                  value={form.startDate}
                  onChange={(v) => update("startDate", v)}
                  disabled={locked}
                  required
                />
                <MonthYearPicker
                  label="Expiry Month & Year"
                  value={form.endDate}
                  onChange={(v) => update("endDate", v)}
                  disabled={locked}
                  required
                />
              </div>

              <ModalInputField
                label="Additional Info"
                placeholder="Skills covered, scope, achievement details..."
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                disabled={isViewOnly}
                required
              />

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                  Certificate Document / Badge{" "}
                  <span className="text-red-500">*</span>
                </label>
                <FileUpload
                  category="certificate"
                  value={certificate}
                  onUpload={setCertificate}
                  onClear={() => setCertificate(null)}
                  disabled={locked}
                />
                {!locked && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Required for verification. Upload your certificate or badge
                    document (PDF, PNG, JPG &le; 5 MB).
                  </p>
                )}
                {certificate?.s3Key && (
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const res = await createDownloadUrl(
                            certificate.s3Key,
                          );
                          const url = res?.url || res?.downloadUrl;
                          if (url) {
                            window.open(url, "_blank", "noopener,noreferrer");
                          } else {
                            throw new Error("Unable to retrieve file URL.");
                          }
                        } catch (err) {
                          setError(err.message || "Failed to open document.");
                        }
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 text-xs font-semibold transition-colors shadow-xs cursor-pointer"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <span>View Uploaded Document</span>
                    </button>
                  </div>
                )}
              </div>

              {error && (
                <div className="p-3.5 rounded-lg bg-red-50 border border-red-200 text-xs font-medium text-red-700">
                  {error}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            {isViewOnly ? (
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/80 flex items-center justify-between gap-3 rounded-b-2xl shrink-0">
                <span
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md border ${
                    isVerified
                      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                      : "text-slate-700 bg-slate-100 border-slate-200"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${isVerified ? "bg-emerald-600" : "bg-slate-400"}`}
                  />
                  {isVerified ? "Verified • View-only" : "View-only"}
                </span>
                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => navigate(`/credentials/edit/other/${id}`)}
                    className="px-4 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs sm:text-sm font-semibold transition-colors shadow-xs cursor-pointer"
                  >
                    {isVerified ? "Edit Info" : "Edit Details"}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-xs sm:text-sm font-semibold shadow-sm transition-colors cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/80 flex items-center justify-between gap-3 rounded-b-2xl shrink-0">
                <div>
                  {isEdit && (
                    <button
                      type="button"
                      onClick={() => navigate(`/credentials/view/other/${id}`)}
                      className="px-3.5 py-2 rounded-lg border border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100 text-xs sm:text-sm font-semibold transition-colors shadow-xs cursor-pointer inline-flex items-center gap-1.5"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <span>View details</span>
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-5 py-2.5 rounded-lg border border-slate-300 bg-white text-xs sm:text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200 transition-colors shadow-sm cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2.5 rounded-lg bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-xs sm:text-sm font-semibold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#2563eb]/30 cursor-pointer"
                  >
                    {submitting
                      ? "Saving..."
                      : proposing
                        ? "Continue to Fee"
                        : isEdit && isVerified
                          ? "Save Additional Info"
                          : isEdit
                            ? "Save changes"
                            : "Add credential"}
                  </button>
                </div>
              </div>
            )}
          </form>
        )}
      </ModalShell>
    </AppShell>
  );
}
